import React, { useState } from "react";
import { Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import { type ConnectionEditState, type ConnectionEditStateUpdater } from "@onetasklist/shared";
import {
  useAsanaSourceSettingsText,
  useAsanaSourceSettingsViewModel,
} from "@onetasklist/shared/sources/asana";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";

interface AsanaSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const AsanaSourceSettings: React.FC<AsanaSourceSettingsProps> = ({
  editingConnection,
  onUpdate,
  sectionOffset,
}) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useAsanaSourceSettingsText(t);
  const vm = useAsanaSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [workspaceQuery, setWorkspaceQuery] = useState("");
  const [projectsQuery, setProjectsQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  if (vm.hasError) {
    return <Text>{text.loadErrorText}</Text>;
  }

  return (
    <SettingsSectionCard
      sectionOffset={sectionOffset}
      title={t(
        "board.connections.catalogue.sourceSettings.asana.sectionWorkspace",
      )}
    >
      <Field
        orientation="horizontal"
        label={text.workspaceLabelText}
        hint={text.workspaceHintText}
      >
        <SourceSingleTagPicker
          selectedId={
            vm.selectedWorkspaceGid === vm.noneOptionValue
              ? undefined
              : vm.selectedWorkspaceGid
          }
          selectedLabel={vm.selectedWorkspaceName}
          query={workspaceQuery}
          placeholder={text.workspacePlaceholderText}
          options={vm.workspaces.map((workspace) => ({
            id: workspace.gid,
            label: workspace.name ?? workspace.gid,
          }))}
          onQueryChange={setWorkspaceQuery}
          onSelect={vm.onSelectWorkspace}
          clearSelectionText={text.workspacePlaceholderText}
          isLoading={!vm.workspacesLoaded}
        />
      </Field>

      {vm.selectedWorkspaceGid !== vm.noneOptionValue && (
        <>
          <Field
            orientation="horizontal"
            label={text.projectsLabelText}
            hint={text.projectsHintText}
          >
            <SourceMultiTagPicker
              selectedIds={vm.selectedProjectGids}
              query={projectsQuery}
              placeholder={text.projectsPlaceholderText}
              options={vm.projects.map((project) => ({
                id: project.gid,
                label: project.name ?? project.gid,
              }))}
              onQueryChange={setProjectsQuery}
              onSelectionChange={vm.onSelectProjects}
              minQueryLength={0}
              typeToSearchText={text.projectsHintText}
              isLoading={!vm.projectsLoaded}
            />
          </Field>

          <Field orientation="horizontal" label={text.assignedToMeLabelText}>
            <Text size={200}>{text.assignedToMeHintText}</Text>
          </Field>
        </>
      )}
    </SettingsSectionCard>
  );
};
